import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RequireAdmin } from '@/components/RequireAdmin';

import { MarketingLayout } from '@/pages/marketing/MarketingLayout';
import { Landing } from '@/pages/marketing/landing/Landing';
import { Lumen } from '@/pages/marketing/lumen/Lumen';
import { Pulse } from '@/pages/marketing/pulse/Pulse';
import { MindFlow } from '@/pages/marketing/mindflow/MindFlow';
import { ProjectFlow } from '@/pages/marketing/projectflow/ProjectFlow';
import { Kinetix } from '@/pages/marketing/kinetix/Kinetix';
import { BlogIndex } from '@/pages/marketing/blog/BlogIndex';
import { BlogPostPage } from '@/pages/marketing/blog/BlogPostPage';
import { Privacy } from '@/pages/marketing/legal/Privacy';

// Ad landing page: split into its own chunk so paid traffic downloads only
// this page, and its stylesheet stays out of the main bundle.
const SocialMarketing = lazy(() =>
  import('@/pages/marketing/smm/SocialMarketing').then((m) => ({ default: m.SocialMarketing })),
);

import { Login } from '@/pages/admin/Login';
import { Dashboard } from '@/pages/admin/Dashboard';
import { Leads } from '@/pages/admin/Leads';
import { Messages } from '@/pages/admin/Messages';
import { BlogList } from '@/pages/admin/BlogList';
import { BlogEditor } from '@/pages/admin/BlogEditor';
import { NewsletterList } from '@/pages/admin/NewsletterList';
import { ServicesAdmin } from '@/pages/admin/ServicesAdmin';
import { TeamAdmin } from '@/pages/admin/TeamAdmin';
import { Analytics } from '@/pages/admin/Analytics';

export function App(): React.JSX.Element {
  return (
    <Routes>
      {/* Public marketing site — light theme, shared Nav/Footer. */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/lumen" element={<Lumen />} />
        <Route path="/pulse" element={<Pulse />} />
        <Route path="/mindflow" element={<MindFlow />} />
        <Route path="/projectflow" element={<ProjectFlow />} />
        <Route path="/kinetix" element={<Kinetix />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/privacy" element={<Privacy />} />
      </Route>

      {/* Standalone landing page for social media & ads — own header/footer. */}
      <Route
        path="/social-media-marketing"
        element={
          <Suspense fallback={<div style={{ minHeight: '100vh', background: '#14183D' }} />}>
            <SocialMarketing />
          </Suspense>
        }
      />

      <Route path="/admin/login" element={<Login />} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="messages" element={<Messages />} />
        <Route path="blog" element={<BlogList />} />
        <Route path="blog/new" element={<BlogEditor />} />
        <Route path="blog/:id/edit" element={<BlogEditor />} />
        <Route path="newsletter" element={<NewsletterList />} />
        <Route path="services" element={<ServicesAdmin />} />
        <Route path="team" element={<TeamAdmin />} />
        <Route path="analytics" element={<Analytics />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
